import { Schema } from 'effect';

import { DateSerializableFromString } from '../datetime/DateSerializableFromString';
import { DateTimeSerializableFromString } from '../DateTimeSerializableFromString';
import { overrideSchema } from '../openapi/overrideSchema';
import { ArticleIndexFavourite } from './ContentSchema';
import { Banner } from './MiscSchema';

/**
 * Product fields whose full generated closures intentionally remain tenant
 * supplied. Product detail fans out through product, taxon, image, and
 * association trees; the RcProfi app restores those exact schemas in
 * `productTightened.ts`.
 */
export type ProductOverrideSlots =
  | 'Product.brand'
  | 'Product.videos'
  | 'Product.mainTaxon'
  | 'Product.images'
  | 'Product.attributes'
  | 'Product.publicDocuments'
  | 'Product.breadcrumbs'
  | 'Product.productAssociations'
  | 'Product.suggestions'
  | 'Product.sets'
  | 'ProductBase.variants'
  | 'ProductBase.mainImage';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);
const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const NullableString = Schema.Union([Schema.String, Schema.Null]);
const ReadOnlyInt = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
);

const ProductIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Product.iri'))
);
const ProductId = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt()).pipe(Schema.brand('Product.id'))
);
export const ProductNameText = Schema.String.pipe(Schema.brand('Product.name'));
export type ProductNameText = typeof ProductNameText.Type;
const ProductName = Schema.Union([ProductNameText, Schema.Null]);
const ProductSlug = Schema.Union([Schema.String.pipe(Schema.brand('Product.slug')), Schema.Null]);

/** Branded product code — route boundaries can construct it with `.make()`. */
export const ProductCode = Schema.String.pipe(Schema.brand('Product.code'));
export type ProductCode = typeof ProductCode.Type;

/** Branded frontend router URL — the sibling entity schemas already brand this field. */
export const ProductRouterUrl = Schema.String.annotate({
  description: 'Frontend router URL for this resource.',
  readOnly: true,
}).pipe(Schema.brand('Product.routerUrl'));
export type ProductRouterUrl = typeof ProductRouterUrl.Type;

/** The age limit a product declares for sale. */
export const ProductAgeRestriction = Schema.Literals(['none', '18+']);
export type ProductAgeRestriction = typeof ProductAgeRestriction.Type;

const ProductVariantCouponCodeBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String.annotate({ description: 'The promotion code.' }),
  promotedPrice: Schema.Finite.annotate({
    description: 'Price after applying the promotion.',
  }).check(Schema.isInt()),
  style: Schema.Literals(['none', 'clickable', 'static']),
  salePercentage: Schema.Finite.check(Schema.isFinite()),
  couponExpiresAt: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
});

const ProductVariantLabelBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String,
  color: Schema.String,
  textColor: Schema.String,
  icon: NullableString,
  name: NullableString,
});

const ProductVariantLocalWarehouseBase = Schema.Struct({
  type: Schema.Union([
    Schema.Literals([
      'KAVAN',
      'ASTRA',
      'CONQUEST',
      'DVEDETI',
      'CARMODEL',
      'CORFIX',
      'HOREJSI',
      'RCSALE',
      'PROMODELS',
      'MPM',
      'AMEWI',
      'PCE',
      'CRU',
    ]),
    Schema.Null,
  ]).annotate({ description: 'Kód skladu nebo typ položky (např. PCE, CRU).' }),
  quantity: Schema.Union([Schema.Finite.check(Schema.isInt()), Schema.Null]).annotate({
    description: 'Dostupné množství položky ve skladu, null pro neomezený počet.',
  }),
  dispatchDate: Schema.optionalKey(Schema.Union([DateSerializableFromString, Schema.Null])),
});

export const ProductShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  ageRestriction: ProductAgeRestriction,
  brand: Schema.Unknown,
  warrantyMonths: Schema.Union([Schema.Finite.check(Schema.isInt()), Schema.Null]),
  ean: NullableString,
  videos: Schema.Array(Schema.Unknown),
  mainTaxon: Schema.Unknown,
  averageRating: Schema.Finite.check(Schema.isFinite()),
  images: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
  id: ReadOnlyInt,
  code: Schema.String,
  attributes: Schema.Array(Schema.Unknown),
  createdAt: Schema.optionalKey(DateTimeSerializableFromString),
  updatedAt: Schema.optionalKey(Schema.Union([DateTimeSerializableFromString, Schema.Null])),
  publicDocuments: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
  metaTitle: NullableString,
  metaDescription: NullableString,
  metaKeywords: NullableString,
  subtitle: NullableString,
  name: NullableString,
  description: NullableString,
  slug: NullableString,
  shortDescription: NullableString,
  routerUrl: Schema.String.annotate({
    description: 'Frontend router URL for this resource.',
    readOnly: true,
  }),
  banners: Schema.optionalKey(Schema.Array(Banner).annotate({ readOnly: true })),
  breadcrumbs: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
  articles: Schema.optionalKey(Schema.Array(ArticleIndexFavourite).annotate({ readOnly: true })),
  productAssociations: Schema.optionalKey(Schema.Unknown.annotate({ readOnly: true })),
  suggestions: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
  sets: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
});

export const ProductIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  '@context': JsonLdContext,
  ageRestriction: ProductAgeRestriction,
  averageRating: Schema.Finite.check(Schema.isFinite()),
  id: ReadOnlyInt,
  code: Schema.String,
  variants: Schema.Array(Schema.Unknown),
  createdAt: Schema.optionalKey(DateTimeSerializableFromString),
  updatedAt: Schema.optionalKey(Schema.Union([DateTimeSerializableFromString, Schema.Null])),
  mainImage: Schema.optionalKey(Schema.Unknown.annotate({ readOnly: true })),
  metaTitle: NullableString,
  subtitle: NullableString,
  name: NullableString,
  slug: NullableString,
  shortDescription: NullableString,
  routerUrl: Schema.String.annotate({
    description: 'Frontend router URL for this resource.',
    readOnly: true,
  }),
});

export const ProductVariantBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  couponCodes: Schema.Array(ProductVariantCouponCodeBase),
  labels: Schema.Array(ProductVariantLabelBase),
  tracked: Schema.Boolean.annotate({ default: false }),
  code: Schema.String,
  enabled: Schema.Boolean.annotate({ default: true }),
  available: Schema.optionalKey(Schema.Boolean.annotate({ readOnly: true })),
  maxOrderable: Schema.optionalKey(
    Schema.Union([Schema.Finite.check(Schema.isInt()), Schema.Null]).annotate({ readOnly: true })
  ),
  inStock: Schema.optionalKey(Schema.Boolean.annotate({ readOnly: true })),
  dispatchDate: Schema.optionalKey(Schema.Union([DateSerializableFromString, Schema.Null])),
  stockStatus: Schema.optionalKey(
    Schema.Literals(['inStock', 'soldOut', 'preorder', 'supplierStock']).annotate({
      readOnly: true,
    })
  ),
  localWarehouse: Schema.optionalKey(
    Schema.Array(ProductVariantLocalWarehouseBase).annotate({ readOnly: true })
  ),
  isFavourite: Schema.optionalKey(
    Schema.Boolean.annotate({
      description:
        'True if ShopUser is logged in and has ProductVariant in at least one favourite list.',
      readOnly: true,
    })
  ),
  price: Schema.optionalKey(Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())),
  priceWithoutTax: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
  ),
  originalPrice: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
  ),
  lowestPriceBeforeDiscount: Schema.optionalKey(
    Schema.Union([Schema.Finite.check(Schema.isInt()), Schema.Null]).annotate({
      default: null,
      readOnly: true,
    })
  ),
  productQuantity: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
  ),
});

export const Product = overrideSchema(ProductShowBase, {
  '@id': ProductIri,
  id: ProductId,
  code: ProductCode,
  name: ProductName,
  slug: ProductSlug,
  routerUrl: ProductRouterUrl,
}).annotate({ identifier: 'Product' });
export type Product = Schema.Schema.Type<typeof Product>;

export const ProductBase = overrideSchema(ProductIndexBase, {
  '@id': ProductIri,
  id: ProductId,
  code: ProductCode,
  name: ProductName,
  slug: ProductSlug,
  routerUrl: ProductRouterUrl,
}).annotate({ identifier: 'ProductBase' });
export type ProductBase = Schema.Schema.Type<typeof ProductBase>;

export const ProductVariant = overrideSchema(ProductVariantBase, {
  '@id': Schema.optionalKey(
    Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('ProductVariant.iri'))
  ),
  code: Schema.String.pipe(Schema.brand('ProductVariant.code')),
  price: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('ProductVariant.price'))
  ),
  priceWithoutTax: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('ProductVariant.priceWithoutTax'))
  ),
  originalPrice: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('ProductVariant.originalPrice'))
  ),
  lowestPriceBeforeDiscount: Schema.optionalKey(
    Schema.Union([
      Schema.Finite.check(Schema.isInt()).pipe(
        Schema.brand('ProductVariant.lowestPriceBeforeDiscount')
      ),
      Schema.Null,
    ]).annotate({ default: null, readOnly: true })
  ),
  stockStatus: Schema.optionalKey(
    Schema.Literals(['inStock', 'soldOut', 'preorder', 'supplierStock'])
      .annotate({ readOnly: true })
      .pipe(Schema.brand('ProductVariant.stockStatus'))
  ),
}).annotate({ identifier: 'ProductVariant' });
export type ProductVariant = Schema.Schema.Type<typeof ProductVariant>;
